import {ChangeDetectionStrategy, Component, ElementRef, Injectable, input, OnInit, signal, viewChild} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {MatChipGrid, MatChipInput, MatChipRemove, MatChipRow} from '@angular/material/chips';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatOption} from '@angular/material/select';
import {TranslateModule} from '@ngx-translate/core';
import {i18n} from 'felles-ng2/tjenester/i18n/Oversettelser';
import {Avtalestatus, AvtaleRessursClient, SpørreskjemaAvtaleModell} from 'felles-ng2/tjenester/ressurser/generertNg2HttpKlient';

/** Maks antall forslag som vises i nedtrekkslisten */
const maksAntallForslag = 20;

/**
 * Valg av ett eller flere avtaler, vhja søk på avtalenavn. Valgte avtaler vises som chips.
 * Avtaler med utløpt prøveperiode og terminerte avtaler kan velges, men er merket med status.
 */
@Component({
    selector: 'velg-avtaler',
    templateUrl: 'velg-avtaler.html',
    styleUrl: 'velg-avtaler.scss',
    host: {'data-testid': 'velg-avtaler'},
    imports: [
        ReactiveFormsModule,
        MatAutocomplete,
        MatAutocompleteTrigger,
        MatChipGrid,
        MatChipInput,
        MatChipRemove,
        MatChipRow,
        MatFormField,
        MatIcon,
        MatLabel,
        MatOption,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VelgAvtalerKomponent implements OnInit {

    /** Valgte avtaler */
    skjemakontroll = input.required<FormControl<SpørreskjemaAvtaleModell[]>>();

    readonly tilgjengeligeAvtaler = signal<SpørreskjemaAvtaleModell[]>([]);
    readonly soek = new FormControl<string>('', {nonNullable: true});
    private readonly søkefelt = viewChild.required<ElementRef<HTMLInputElement>>('soekefelt');
    private readonly søketekst = toSignal(this.soek.valueChanges, {initialValue: ''});

    constructor(private avhengigheter: VelgAvtalerAvhengigheter) {}

    async ngOnInit(): Promise<void> {
        this.tilgjengeligeAvtaler.set(await this.avhengigheter.finnAlle());
    }

    valgteAvtaler(): SpørreskjemaAvtaleModell[] {
        return this.skjemakontroll().value;
    }

    /** Avtaler som ikke allerede er valgt og som har navn som inneholder søketeksten */
    forslag(): SpørreskjemaAvtaleModell[] {
        const søketekst = (typeof this.søketekst() === 'string' ? this.søketekst() : '').trim().toLowerCase();
        const valgte = new Set(this.valgteAvtaler().map(avtale => avtale.id));
        return this.tilgjengeligeAvtaler()
          .filter(avtale => !valgte.has(avtale.id) && avtale.navn.toLowerCase().includes(søketekst))
          .slice(0, maksAntallForslag);
    }

    leggTil(event: MatAutocompleteSelectedEvent): void {
        const avtale = event.option.value as SpørreskjemaAvtaleModell;
        this.skjemakontroll().setValue([...this.valgteAvtaler(), avtale]);
        this.skjemakontroll().markAsDirty();
        //som i Angular Material sitt eksempel for chips med autocomplete: tøm input-feltet og fjern valget i autocomplete
        this.søkefelt().nativeElement.value = '';
        this.soek.setValue('');
        event.option.deselect();
    }

    fjern(avtale: SpørreskjemaAvtaleModell): void {
        this.skjemakontroll().setValue(this.valgteAvtaler().filter(valgt => valgt.id !== avtale.id));
        this.skjemakontroll().markAsDirty();
    }

    /** Status som vises for avtaler som ikke er aktive eller i prøveperiode, ellers null */
    statustekst(avtale: SpørreskjemaAvtaleModell): string | null {
        switch (avtale.avtalestatus) {
            case Avtalestatus.prøveperiodeUtløpt: return i18n('Prøveperiode utløpt');
            case Avtalestatus.terminert: return i18n('Terminert');
            default: return null;
        }
    }
}

@Injectable()
export class VelgAvtalerAvhengigheter {
    constructor(private avtaleRessurs: AvtaleRessursClient) {}

    /** Alle avtaler, sortert på navn */
    async finnAlle(): Promise<SpørreskjemaAvtaleModell[]> {
        const avtaler = await this.avtaleRessurs.finnAlle().somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke hente avtaler'));
        return avtaler
          .map(avtale => ({id: avtale.id, navn: avtale.navn, avtalestatus: avtale.avtalestatus}))
          .sort((a, b) => a.navn.localeCompare(b.navn));
    }
}
